// campaigns.js
// Shared helpers for the Campaigns feature.
//
// A "campaign" is a reusable Firestore doc that configures:
//   - what prints on the thermal ticket face (tagline, expiry text, serial)
//   - what the QR code lands on (landing headline/body/buttons, or a linked pancake)
//   - print-history rollup (batches + token refs) for light analytics
//
// Tokens created from a campaign carry `ticketType: "promo"` so they render
// through the existing promo/keepsake landing flow — the new CampaignLanding
// page (Slice 3B) will branch on whether the token has `orderId` set.
//
// v1 is print-only. v2 fields (delivery, recipients, landingMode) are reserved
// with defaults so no migration is needed to ship email invitations later.

import { db } from "../firebase";
import {
  collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc,
  query, orderBy, serverTimestamp, increment, arrayUnion,
} from "firebase/firestore";
import { printJobs } from "./printer";

// Canonical shape for a brand-new campaign. Callers may override any field.
export function blankCampaign() {
  return {
    // Identity
    name:             "Untitled campaign",
    archived:         false,

    // Ticket face (what prints)
    ticketTagline:    "You got handed a Golden Ticket.",
    ticketExpiryText: "",       // display-only; auto-fills from expiration mode when blank
    serialPrefix:     "GT",

    // Landing page content (shown when scanned + no linked pancake)
    headline:         "",
    body:             "",
    heroImageUrl:     null,
    buttons:          [],   // [{ label, url }] — max 4

    // Optional pancake attachment
    linkedOrderId:    null,

    // Expiration — actually enforced on scan (Slice 3B). Three modes:
    //   never    → tokens never expire
    //   duration → each token expires (now + expirationMinutes) at print time
    //   absolute → all tokens share a fixed expirationDate
    expirationMode:    "never",     // "never" | "duration" | "absolute"
    expirationMinutes: 1440,        // duration mode: minutes from print
    expirationDate:    null,        // absolute mode: ISO string

    // v2 reservations — unused in v1 but shape is stable
    delivery:         "print",       // "print" | "email" | "both" (v2)
    recipients:       [],            // [{ name, email, sentAt, tokenId }] (v2)
    landingMode:      "discovery",   // "discovery" | "invitation" (v2)

    // Analytics rollup
    totalPrinted:     0,
    totalScanned:     0,
    lastScannedAt:    null,
    printBatches:     [],            // [{ batchLabel, qty, printedAt, tokenIds[] }]
  };
}

// Human-readable expiration label from a campaign config. Used as:
//   - placeholder in the editor's ticketExpiryText field
//   - fallback on the printed ticket when ticketExpiryText is blank
export function computeExpiryDisplay(campaign) {
  if (!campaign) return "";
  if (campaign.expirationMode === "never") return "NO EXPIRY";
  if (campaign.expirationMode === "absolute" && campaign.expirationDate) {
    const d = new Date(campaign.expirationDate);
    if (isNaN(d)) return "";
    return `EXPIRES ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase()}`;
  }
  if (campaign.expirationMode === "duration") {
    const m = campaign.expirationMinutes || 0;
    if (m <= 0)        return "";
    if (m < 60)        return `VALID FOR ${m} MIN`;
    if (m < 1440)      return `VALID FOR ${Math.round(m / 60)} HR${m >= 120 ? "S" : ""}`;
    if (m < 10080)     return `VALID FOR ${Math.round(m / 1440)} DAY${m >= 2880 ? "S" : ""}`;
    if (m < 43200)     return `VALID FOR ${Math.round(m / 10080)} WEEK${m >= 20160 ? "S" : ""}`;
    if (m < 525600)    return `VALID FOR ${Math.round(m / 43200)} MONTH${m >= 86400 ? "S" : ""}`;
    return `VALID FOR ${Math.round(m / 525600)} YEAR${m >= 1051200 ? "S" : ""}`;
  }
  return "";
}

// Resolve each token's actual expiresAt based on campaign config.
// Returns a Date or null. Called at print time per token.
export function resolveTokenExpiry(campaign, now = Date.now()) {
  if (!campaign) return null;
  if (campaign.expirationMode === "never") return null;
  if (campaign.expirationMode === "absolute") {
    return campaign.expirationDate ? new Date(campaign.expirationDate) : null;
  }
  if (campaign.expirationMode === "duration") {
    const m = campaign.expirationMinutes || 0;
    if (m <= 0) return null;
    return new Date(now + m * 60 * 1000);
  }
  return null;
}

// List all campaigns newest-first. Archived included; caller filters.
export async function listCampaigns() {
  const snap = await getDocs(query(collection(db, "campaigns"), orderBy("updatedAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getCampaign(id) {
  const snap = await getDoc(doc(db, "campaigns", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Create a fresh campaign. Returns the new ID.
export async function createCampaign(partial = {}) {
  const id  = crypto.randomUUID();
  const now = serverTimestamp();
  await setDoc(doc(db, "campaigns", id), {
    ...blankCampaign(),
    ...partial,
    id,
    createdAt: now,
    updatedAt: now,
  });
  return id;
}

// Persist edits. Strips undefined. Touches updatedAt.
export async function saveCampaign(id, updates) {
  const clean = Object.fromEntries(
    Object.entries(updates).filter(([, v]) => v !== undefined)
  );
  await updateDoc(doc(db, "campaigns", id), {
    ...clean,
    updatedAt: serverTimestamp(),
  });
}

// Duplicate a campaign, preserving settings but resetting rollup + archive flag.
export async function duplicateCampaign(id) {
  const src = await getCampaign(id);
  if (!src) throw new Error("Campaign not found");
  const { id: _id, createdAt, updatedAt, totalPrinted, totalScanned, lastScannedAt, printBatches, archived, ...rest } = src;
  return createCampaign({
    ...rest,
    name: `${src.name} (copy)`,
  });
}

export async function archiveCampaign(id, archived = true) {
  await saveCampaign(id, { archived });
}

export async function deleteCampaign(id) {
  await deleteDoc(doc(db, "campaigns", id));
}

// ── PRINT FLOW ─────────────────────────────────────────────────────────────
// Creates N tokens for a campaign and sends them to the thermal printer.
// Writes a print-batch record back onto the campaign for later analytics.
// Reuses the existing "promo" layout in buildPrintBuffer — campaigns are
// visually identical to today's Secret Tickets.
//
// Returns the array of token IDs that were created.
export async function printCampaignTokens(campaign, qty, options = {}) {
  const {
    batchLabel = null,
    onStatus   = () => {},
    subtitle   = null,   // overrides "Scan to reveal your pancake" on ticket
  } = options;

  if (!campaign?.id) throw new Error("Campaign required");
  if (!Number.isFinite(qty) || qty < 1) throw new Error("qty must be >= 1");

  onStatus({ pct: 10, msg: `Creating ${qty} token${qty > 1 ? "s" : ""}...` });

  const tokenIds = [];
  const jobs     = [];
  const orderCatalogNumber = await resolveCatalogNumber(campaign.linkedOrderId);
  const printDisplay = (campaign.ticketExpiryText?.trim()) || computeExpiryDisplay(campaign);

  for (let i = 0; i < qty; i++) {
    const tokenId = `cmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${i}`;
    tokenIds.push(tokenId);

    // Each token gets its own expiresAt. Duration mode = now + offset per token;
    // absolute mode = shared date; never = null.
    const expiresAt = resolveTokenExpiry(campaign);

    await setDoc(doc(db, "tokens", tokenId), {
      id: tokenId,
      status: "used",
      ticketType: "promo",
      source: "campaign",
      campaignId: campaign.id,
      orderId: campaign.linkedOrderId || null,
      batchLabel: batchLabel || null,
      catalogNumber: orderCatalogNumber,
      // Snapshot campaign face at print time so later edits don't change
      // what's already been handed out in the wild.
      snapshotTagline:      campaign.ticketTagline || null,
      snapshotExpiryText:   printDisplay || null,
      snapshotSerialPrefix: campaign.serialPrefix || "GT",
      // Actual expiration enforcement (Slice 3B will read this on landing).
      expiresAt:            expiresAt || null,
      // Scan tracking — populated by the landing page (Slice 3B).
      scannedAt: null,
      scanCount: 0,
      lastScannedAt: null,
      createdAt: serverTimestamp(),
    });

    jobs.push({
      ticketType: "promo",
      orderUrl: `https://thejoyofpancakes.com/order?t=${tokenId}`,
      expiryDisplay: printDisplay,
      tagline: campaign.ticketTagline || "",
      serial: `${campaign.serialPrefix || "GT"}-${String(orderCatalogNumber ?? Math.floor(Math.random() * 9999)).padStart(4, "0")}`,
      subtitle: subtitle || (campaign.linkedOrderId ? "Scan to reveal your pancake" : "Scan to learn more"),
      // Unlinked campaigns get a footer that doesn't promise a pancake.
      hasPancake: !!campaign.linkedOrderId,
    });
  }

  onStatus({ pct: 40, msg: "Sending to printer..." });
  await printJobs(jobs, { onStatus });

  // Record the batch on the campaign for rollup.
  await updateDoc(doc(db, "campaigns", campaign.id), {
    totalPrinted: increment(qty),
    updatedAt:    serverTimestamp(),
    printBatches: arrayUnion({
      batchLabel: batchLabel || null,
      qty,
      printedAt:  new Date().toISOString(), // Firestore arrayUnion can't take serverTimestamp
      tokenIds,
    }),
  });

  return tokenIds;
}

// Fire-and-forget scan tracking. Called from landing pages on mount. Bumps the
// token's scan counters and the campaign's rollup. Silently swallows errors —
// tracking failures must never block the landing page from rendering.
export async function trackCampaignScan(tokenId, campaignId) {
  if (!tokenId) return;
  const now = serverTimestamp();
  try {
    const tokenRef = doc(db, "tokens", tokenId);
    const tokenSnap = await getDoc(tokenRef);
    const firstScan = tokenSnap.exists() && !tokenSnap.data().scannedAt;
    await updateDoc(tokenRef, {
      scanCount: increment(1),
      lastScannedAt: now,
      ...(firstScan ? { scannedAt: now } : {}),
    });
  } catch (e) { /* rules may reject; ok */ }

  if (!campaignId) return;
  try {
    await updateDoc(doc(db, "campaigns", campaignId), {
      totalScanned: increment(1),
      lastScannedAt: now,
    });
  } catch (e) { /* ok */ }
}

async function resolveCatalogNumber(orderId) {
  if (!orderId) return null;
  try {
    const snap = await getDoc(doc(db, "orders", orderId));
    return snap.data()?.catalogNumber ?? null;
  } catch {
    return null;
  }
}
